/**
 * useDirectMessageToasts — polls the user's direct threads every 30s and
 * fires a soft "new direct message" toast when someone else posts to a thread
 * the user is on. Non-blocking, auto-dismisses, and silently seeds state on
 * first load so you don't get a blast of toasts on every login.
 *
 * Intentionally polling (not WebSocket) — simple, reliable, and the existing
 * backend already provides the right summary endpoint.
 *
 * Feb 2026 — user asked for in-app notifications so folks sitting in the
 * portal all day don't have to switch to email to see new direct replies.
 */
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "../components/ui/sonner";
import { useAuth } from "../contexts/AuthContext";
import { listDirectThreads } from "../lib/api";

const POLL_MS = 30000;

export default function useDirectMessageToasts() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Snapshot of thread_id → last_activity_at from the most recent poll.
  // Lives in a ref so re-renders don't reset it and we skip the first-run blast.
  const seenRef = useRef(null);
  // Keep latest location in a ref so the poll closure can suppress toasts
  // for the thread the user is currently viewing.
  const locationRef = useRef(location);
  useEffect(() => { locationRef.current = location; }, [location]);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    let timer = null;

    const tick = async () => {
      try {
        const { data } = await listDirectThreads();
        if (cancelled) return;
        const threads = Array.isArray(data) ? data : [];
        // First run after mount: seed, do NOT toast.
        if (seenRef.current === null) {
          const seed = {};
          for (const t of threads) {
            seed[t.id] = t.last_activity_at;
          }
          seenRef.current = seed;
          return;
        }
        const next = {};
        for (const t of threads) {
          const prev = seenRef.current[t.id];
          const changed = prev !== t.last_activity_at;
          const authoredByMe = String(t.last_activity_by_id || "") === String(user.id);
          const openedHere = locationRef.current.pathname === `/direct/${t.id}`;
          if (changed && !authoredByMe && !openedHere) {
            // Human sender label — fallback keeps the toast clean if the
            // backend ever omits author info.
            const senderLabel = t.last_activity_by_name
              || (t.last_activity_by_role === "rep" ? "A rep" : "A teammate");
            const subject = t.subject || "(no subject)";
            toast(`💬 New direct message from ${senderLabel}`, {
              description: subject,
              action: {
                label: "Open",
                onClick: () => navigate(`/direct/${t.id}`),
              },
              duration: 10000,
            });
          }
          next[t.id] = t.last_activity_at;
        }
        seenRef.current = next;
      } catch (_e) {
        // Silent fail — polling noise shouldn't surface as user-facing errors.
      }
    };

    // Fire once immediately so we seed on page load, then every POLL_MS.
    tick();
    timer = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
    // Re-run when the logged-in user changes identity (e.g. after switch-user).
  }, [user?.id, navigate]);
}
